import './sign-in.css';
import type { SurfaceRenderer } from '../host.js';
import type { AuthOptions } from '../kernel-contract.js';
import { renderHero, type HeroProps } from '../components/hero.js';
import { mailIcon, lockIcon, eyeIcon, eyeOffIcon, shieldIcon } from './sign-in-icons.js';

/**
 * The sign-in surface (CAOS-132) — one of the kernel's two frozen pre-install surfaces, shown
 * on the no-session decision. It is `login-mockup.png` made real: the shared branded hero
 * (CAOS-131, `radar` motif) on the left, a floating white card on the right built from `@caos/ui`
 * primitives (`<caos-text-field>`, `<caos-button>`), styled ONLY through `--caos-*` tokens so a
 * five-token white-label repaints it whole (accent AND the visible wordmark). No baked colour,
 * no inline style, no external request — CSP (`style-src 'self'`) holds.
 *
 * Auth wiring: the password form POSTs `{ identifier, password }` to the kernel identity core's
 * one unauthenticated bootstrap route (`auth/login`, see `kernel/src/api/auth-routes.ts`) on the
 * SAME origin (`connect-src 'self'`); it invents no parallel auth. The endpoint's base path and
 * the SSO start route are owned by the identity lane (CAOS-021) — the host targets the seam.
 */

/** The `auth/login` bootstrap route, same-origin. Base path is the identity/API lane's to set. */
const LOGIN_ENDPOINT = '/api/auth/login';
/** Same-origin SSO hand-off seam; the provider flow itself is the identity lane's (CAOS-021). */
const SSO_START = (providerId: string): string => `/api/auth/sso/${encodeURIComponent(providerId)}/start`;

/**
 * CAOS-289 — the forgot-password flow is part of the LOGIN CONTRACT: the "Forgot password?"
 * affordance and its reset screens are ALWAYS present on any login surface; a brand may restyle
 * them (they are built from `@caos/ui` and `--caos-*` tokens) but never remove them. The screens
 * live on the sign-in surface and are chosen by route: `/account/recover` (request a link) and
 * `/account/reset?token=…` (set a new password from the emailed link).
 */
const REQUEST_RESET_ENDPOINT = '/api/auth/request-password-reset';
const COMPLETE_RESET_ENDPOINT = '/api/auth/complete-password-reset';
/** The route the "Forgot password?" link points at — the request-a-link screen. */
export const RECOVER_PATH = '/account/recover';
/** The route the emailed link points at — the set-a-new-password screen (token in the query). */
export const RESET_PATH = '/account/reset';
/** The shortest new password the reset screen accepts (mirrors the kernel's server-side floor). */
const MIN_NEW_PASSWORD_LENGTH = 8;

/** Outcome of a reset request or completion — the surface renders a neutral message either way. */
export interface ResetResult {
  readonly ok: boolean;
  readonly message?: string;
}

/** The request-a-reset-link seam. Default POSTs to the kernel; overridable for tests. */
export type ResetRequester = (identifier: string) => Promise<ResetResult>;
/** The complete-a-reset seam. Default POSTs to the kernel; overridable for tests. */
export type ResetCompleter = (token: string, newPassword: string) => Promise<ResetResult>;

const defaultResetRequester: ResetRequester = async (identifier) => {
  try {
    // The kernel ALWAYS answers 200 here (no account-existence disclosure); we surface the same
    // neutral confirmation regardless, so a network failure is the only thing that changes copy.
    await fetch(REQUEST_RESET_ENDPOINT, {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ identifier }),
      credentials: 'same-origin',
    });
    return { ok: true };
  } catch {
    return { ok: false, message: 'Could not reach the reset service. Try again in a moment.' };
  }
};

const defaultResetCompleter: ResetCompleter = async (token, newPassword) => {
  try {
    const res = await fetch(COMPLETE_RESET_ENDPOINT, {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ token, newPassword }),
      credentials: 'same-origin',
    });
    if (res.ok) return { ok: true };
    return { ok: false, message: 'This password reset link is invalid or has expired.' };
  } catch {
    return { ok: false, message: 'Could not reach the reset service. Try again in a moment.' };
  }
};

let resetRequester: ResetRequester = defaultResetRequester;
let resetCompleter: ResetCompleter = defaultResetCompleter;

/** Override the reset seams (production wiring / tests). */
export function setPasswordResetClient(seams: { request?: ResetRequester; complete?: ResetCompleter }): void {
  if (seams.request) resetRequester = seams.request;
  if (seams.complete) resetCompleter = seams.complete;
}

/** The credentials the card collects; `identifier` is the login handler's body field. */
export interface Credentials {
  readonly identifier: string;
  readonly password: string;
}

/** Outcome of an authentication attempt: success (the host navigates) or a display message. */
export interface AuthResult {
  readonly ok: boolean;
  readonly message?: string;
}

/** The password-login seam. Default POSTs to the kernel bootstrap route; overridable for tests. */
export type SignInAuthenticator = (credentials: Credentials) => Promise<AuthResult>;

const defaultAuthenticator: SignInAuthenticator = async ({ identifier, password }) => {
  try {
    const res = await fetch(LOGIN_ENDPOINT, {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ identifier, password }),
      credentials: 'same-origin',
    });
    if (res.ok) {
      // Remember the sign-in identifier so the post-login shell's account block shows the real user
      // (CAOS-267) — the boot decision carries no display name. Best-effort; failure never blocks login.
      try {
        window.sessionStorage.setItem('caos_user_hint', identifier);
      } catch {
        /* sessionStorage unavailable — the account block degrades to no name. */
      }
      // A session was minted; re-boot so the kernel re-resolves the now-authenticated surface.
      window.location.assign('/');
      return { ok: true };
    }
    // The login handler returns a uniform 401 that never discloses which field was wrong.
    return { ok: false, message: 'That email and password combination was not recognized.' };
  } catch {
    return { ok: false, message: 'Could not reach the sign-in service. Try again in a moment.' };
  }
};

let authenticator: SignInAuthenticator = defaultAuthenticator;

/** Override the password-login seam (production wiring / tests). */
export function setSignInAuthenticator(fn: SignInAuthenticator): void {
  authenticator = fn;
}

/** The left-hand hero copy — the sign-in mockup's headline, body, and value-prop grid. */
const SIGN_IN_HERO: HeroProps = {
  motif: 'radar',
  headline: 'The operating system for modern business.',
  body: 'A metadata-driven platform for building and running CRMs, ERPs, CMSs, and any business system from one intelligent core.',
  features: [
    { icon: 'layers', label: 'Metadata first', body: 'Everything is metadata. Models drive behavior. Changes are data.' },
    { icon: 'cube', label: 'Platform core', body: 'One engine. Many systems. Built once, extended endlessly.' },
    { icon: 'nodes', label: 'Compose anything', body: 'Build CRMs, ERPs, portals, or custom apps without rewriting core.' },
    { icon: 'shield', label: 'Enterprise ready', body: 'Secure by design. Auditable, scalable, and built to last.' },
  ],
  // Footer omitted → the hero's brand-neutral default ("KERNEL ONLINE · VERSION 1.0.0 ·
  // WHITE-LABEL READY"), so a white-label does not leave a stale "CAOS" baked in the status line.
};

/** A `<tag>` with class names. */
function el<K extends keyof HTMLElementTagNameMap>(tag: K, ...classes: string[]): HTMLElementTagNameMap[K] {
  const node = document.createElement(tag);
  if (classes.length) node.className = classes.join(' ');
  return node;
}

/** An empty span whose visible text is `content: var(--brand-wordmark)` — repaints on a swap. */
function brandName(): HTMLSpanElement {
  return el('span', 'caos-signin__brand');
}

/** One boxed field: an external mono label + a `<caos-text-field>` with an overlaid leading icon. */
function field(opts: {
  name: string;
  label: string;
  placeholder: string;
  icon: SVGSVGElement;
  password?: boolean;
}): { group: HTMLDivElement; input: HTMLElement } {
  const group = el('div', 'caos-signin__field');
  if (opts.password) group.classList.add('caos-signin__field--password');

  const input = document.createElement('caos-text-field');
  input.classList.add('caos-signin__control');
  input.setAttribute('label', opts.label);
  input.setAttribute('placeholder', opts.placeholder);
  input.setAttribute('name', opts.name);

  const lead = el('span', 'caos-signin__field-icon', 'caos-signin__field-icon--lead');
  lead.append(opts.icon);

  group.append(input, lead);

  if (opts.password) {
    const toggle = el('button', 'caos-signin__reveal');
    toggle.type = 'button';
    toggle.setAttribute('aria-label', 'Show password');
    toggle.setAttribute('aria-pressed', 'false');
    toggle.append(eyeIcon());
    toggle.addEventListener('click', () => {
      const revealed = input.hasAttribute('data-reveal');
      if (revealed) {
        input.removeAttribute('data-reveal');
        toggle.setAttribute('aria-pressed', 'false');
        toggle.setAttribute('aria-label', 'Show password');
        toggle.replaceChildren(eyeIcon());
      } else {
        input.setAttribute('data-reveal', '');
        toggle.setAttribute('aria-pressed', 'true');
        toggle.setAttribute('aria-label', 'Hide password');
        toggle.replaceChildren(eyeOffIcon());
      }
    });
    group.append(toggle);
  }

  return { group, input };
}

/** The "OR" divider between the password form and the SSO options. */
function orDivider(): HTMLDivElement {
  const row = el('div', 'caos-signin__divider');
  row.setAttribute('aria-hidden', 'true');
  const word = el('span', 'caos-signin__divider-label');
  word.textContent = 'OR';
  row.append(el('span', 'caos-signin__divider-line'), word, el('span', 'caos-signin__divider-line'));
  return row;
}

/** Build the floating sign-in card for the given auth options. */
function buildCard(auth: AuthOptions): HTMLElement {
  const card = el('div', 'caos-signin__card');

  const title = el('h1', 'caos-signin__title');
  title.append(document.createTextNode('Sign in to '), brandName());
  const subtitle = el('p', 'caos-signin__subtitle');
  subtitle.textContent = 'Access the platform.';
  card.append(title, subtitle);

  const form = el('form', 'caos-signin__form');
  form.noValidate = true;

  const error = el('p', 'caos-signin__error');
  error.setAttribute('role', 'alert');
  error.setAttribute('aria-live', 'polite');
  error.hidden = true;
  form.append(error);

  if (auth.password) {
    const email = field({
      name: 'identifier',
      label: 'Email',
      placeholder: 'name@company.com',
      icon: mailIcon(),
    });
    const password = field({
      name: 'password',
      label: 'Password',
      placeholder: 'Enter your password',
      icon: lockIcon(),
      password: true,
    });

    const forgotRow = el('div', 'caos-signin__aux');
    const forgot = el('a', 'caos-signin__link');
    forgot.href = '/account/recover';
    forgot.textContent = 'Forgot password?';
    forgotRow.append(forgot);

    const submit = document.createElement('caos-button');
    submit.setAttribute('variant', 'primary');
    submit.setAttribute('block', '');
    submit.setAttribute('type', 'submit');
    submit.classList.add('caos-signin__submit');
    submit.textContent = 'Sign in';

    form.append(email.group, password.group, forgotRow, submit);

    form.addEventListener('submit', (event) => {
      event.preventDefault();
      error.hidden = true;
      const credentials: Credentials = {
        identifier: readValue(email.input),
        password: readValue(password.input),
      };
      submit.setAttribute('loading', '');
      void authenticator(credentials).then((result) => {
        submit.removeAttribute('loading');
        if (!result.ok) {
          error.textContent = result.message ?? 'Sign-in failed. Please try again.';
          error.hidden = false;
          // Clear the password on a failed attempt and return focus to it, so a retry starts
          // clean and no rejected secret lingers in the field (CAOS-206).
          const pw = password.input as HTMLElement & { value: string; focus: () => void };
          pw.value = '';
          pw.focus();
        }
      });
    });
  }

  if (auth.password && auth.sso.length) card.append(form, orDivider());
  else card.append(form);

  for (const provider of auth.sso) {
    const btn = document.createElement('caos-button');
    btn.setAttribute('variant', 'secondary');
    btn.setAttribute('block', '');
    btn.classList.add('caos-signin__sso');
    btn.append(shieldIcon(), document.createTextNode(provider.label));
    btn.addEventListener('click', () => window.location.assign(SSO_START(provider.id)));
    card.append(btn);
  }

  const legal = el('p', 'caos-signin__legal');
  legal.append(document.createTextNode('By signing in, you agree to the '), brandName());
  const terms = el('a', 'caos-signin__link');
  terms.href = '/legal/terms';
  terms.textContent = ' Terms of Service';
  const privacy = el('a', 'caos-signin__link');
  privacy.href = '/legal/privacy';
  privacy.textContent = 'Privacy Policy';
  legal.append(terms, document.createTextNode(' and acknowledge the '), privacy, document.createTextNode('.'));
  card.append(legal);

  return card;
}

// ── CAOS-289: the forgot-password screens (part of the login contract) ────────────────

/** A hidden, polite error line shared by the recover forms. */
function errorLine(): HTMLParagraphElement {
  const error = el('p', 'caos-signin__error');
  error.setAttribute('role', 'alert');
  error.setAttribute('aria-live', 'polite');
  error.hidden = true;
  return error;
}

/** A block primary `<caos-button type=submit>` with the shared submit class. */
function submitButton(label: string): HTMLElement {
  const submit = document.createElement('caos-button');
  submit.setAttribute('variant', 'primary');
  submit.setAttribute('block', '');
  submit.setAttribute('type', 'submit');
  submit.classList.add('caos-signin__submit');
  submit.textContent = label;
  return submit;
}

/** A block primary `<caos-button>` that navigates back to the sign-in surface. */
function backToSignInButton(label = 'Back to sign in'): HTMLElement {
  const btn = document.createElement('caos-button');
  btn.setAttribute('variant', 'primary');
  btn.setAttribute('block', '');
  btn.classList.add('caos-signin__submit');
  btn.textContent = label;
  btn.addEventListener('click', () => window.location.assign('/'));
  return btn;
}

/** A neutral message paragraph (the "check your email" / "password updated" copy). */
function noteLine(text: string): HTMLParagraphElement {
  const note = el('p', 'caos-signin__note');
  note.textContent = text;
  return note;
}

/** A subtle "Back to sign in" text link row under a recover form. */
function backLinkRow(): HTMLDivElement {
  const row = el('div', 'caos-signin__aux');
  const link = el('a', 'caos-signin__link');
  link.href = '/';
  link.textContent = 'Back to sign in';
  row.append(link);
  return row;
}

/** Step 1 — request a reset link (reached from the "Forgot password?" affordance). */
function buildRecoverRequestCard(): HTMLElement {
  const card = el('div', 'caos-signin__card');
  const title = el('h1', 'caos-signin__title');
  title.append(document.createTextNode('Reset your '), brandName());
  const subtitle = el('p', 'caos-signin__subtitle');
  subtitle.textContent = 'Enter your email and we will send you a reset link.';
  card.append(title, subtitle);

  const form = el('form', 'caos-signin__form');
  form.noValidate = true;
  const error = errorLine();
  const email = field({ name: 'identifier', label: 'Email', placeholder: 'name@company.com', icon: mailIcon() });
  const submit = submitButton('Send reset link');
  form.append(error, email.group, submit);
  card.append(form, backLinkRow());

  form.addEventListener('submit', (event) => {
    event.preventDefault();
    error.hidden = true;
    const identifier = readValue(email.input).trim();
    if (!identifier) {
      error.textContent = 'Enter your email address.';
      error.hidden = false;
      return;
    }
    submit.setAttribute('loading', '');
    void resetRequester(identifier).then((result) => {
      submit.removeAttribute('loading');
      if (!result.ok) {
        error.textContent = result.message ?? 'Something went wrong. Please try again.';
        error.hidden = false;
        return;
      }
      // Neutral confirmation — never confirms whether the account exists.
      card.replaceWith(buildRecoverConfirmCard(identifier));
    });
  });
  return card;
}

/** Step 2 — the neutral "if that account exists, a link is on its way" confirmation. */
function buildRecoverConfirmCard(identifier: string): HTMLElement {
  const card = el('div', 'caos-signin__card');
  const title = el('h1', 'caos-signin__title');
  title.textContent = 'Check your email';
  card.append(title);
  card.append(
    noteLine(
      `If an account exists for ${identifier}, a password reset link is on its way. The link expires in 45 minutes.`,
    ),
  );
  card.append(backToSignInButton());
  return card;
}

/** Step 3 — set a new password from the emailed link (token carried in the URL). */
function buildResetCompleteCard(token: string): HTMLElement {
  const card = el('div', 'caos-signin__card');

  if (!token) {
    const title = el('h1', 'caos-signin__title');
    title.textContent = 'Invalid link';
    // Send them to the request screen (not sign-in) — they were mid-reset and need a fresh link.
    const again = document.createElement('caos-button');
    again.setAttribute('variant', 'primary');
    again.setAttribute('block', '');
    again.classList.add('caos-signin__submit');
    again.textContent = 'Request a new link';
    again.addEventListener('click', () => window.location.assign(RECOVER_PATH));
    card.append(title, noteLine('This password reset link is invalid or has expired.'), again);
    return card;
  }

  const title = el('h1', 'caos-signin__title');
  title.textContent = 'Set a new password';
  const subtitle = el('p', 'caos-signin__subtitle');
  subtitle.textContent = 'Choose a new password for your account.';
  card.append(title, subtitle);

  const form = el('form', 'caos-signin__form');
  form.noValidate = true;
  const error = errorLine();
  const pw = field({ name: 'newPassword', label: 'New password', placeholder: 'Enter a new password', icon: lockIcon(), password: true });
  const confirm = field({ name: 'confirmPassword', label: 'Confirm password', placeholder: 'Re-enter the password', icon: lockIcon(), password: true });
  const submit = submitButton('Set new password');
  form.append(error, pw.group, confirm.group, submit);
  card.append(form);

  form.addEventListener('submit', (event) => {
    event.preventDefault();
    error.hidden = true;
    const next = readValue(pw.input);
    const again = readValue(confirm.input);
    if (next.length < MIN_NEW_PASSWORD_LENGTH) {
      error.textContent = `Your new password must be at least ${MIN_NEW_PASSWORD_LENGTH} characters.`;
      error.hidden = false;
      return;
    }
    if (next !== again) {
      error.textContent = 'Those passwords do not match.';
      error.hidden = false;
      return;
    }
    submit.setAttribute('loading', '');
    void resetCompleter(token, next).then((result) => {
      submit.removeAttribute('loading');
      if (!result.ok) {
        error.textContent = result.message ?? 'This password reset link is invalid or has expired.';
        error.hidden = false;
        return;
      }
      card.replaceWith(buildResetDoneCard());
    });
  });
  return card;
}

/** Step 4 — success; send the user back to sign in with their new password. */
function buildResetDoneCard(): HTMLElement {
  const card = el('div', 'caos-signin__card');
  const title = el('h1', 'caos-signin__title');
  title.textContent = 'Password updated';
  card.append(title);
  card.append(noteLine('Your password has been changed. Sign in with your new password.'));
  card.append(backToSignInButton('Sign in'));
  return card;
}

/** Which login-surface view the current route asks for. */
type RecoverView = 'login' | 'request' | 'reset';
function currentView(): RecoverView {
  try {
    const path = window.location.pathname;
    if (path === RESET_PATH) return 'reset';
    if (path === RECOVER_PATH) return 'request';
  } catch {
    /* no location (non-browser) — default to the login card */
  }
  return 'login';
}

/** Read the reset token from the current URL's `?token=` (empty when absent). */
function tokenFromUrl(): string {
  try {
    return new URL(window.location.href).searchParams.get('token') ?? '';
  } catch {
    return '';
  }
}

/** Build the correct aside card for the current route (login / request / reset). */
function buildAside(auth: AuthOptions): HTMLElement {
  switch (currentView()) {
    case 'request':
      return buildRecoverRequestCard();
    case 'reset':
      return buildResetCompleteCard(tokenFromUrl());
    default:
      return buildCard(auth);
  }
}

/** Read the current value of a `<caos-text-field>` (its `value` prop, falling back to the attr). */
function readValue(control: HTMLElement): string {
  const value = (control as unknown as { value?: string }).value;
  return typeof value === 'string' ? value : control.getAttribute('value') ?? '';
}

/**
 * The sign-in surface renderer. Mounts the branded hero BESIDE the sign-in card; a single
 * full-bleed gradient ground spans both so the hero and the card float on one seamless field
 * (the embedded hero's own ground is made transparent for this surface — see sign-in.css).
 */
export const signInSurface: SurfaceRenderer<'sign-in'> = {
  surface: 'sign-in',
  render(decision, ctx) {
    const layout = el('div', 'caos-signin');

    const hero = el('div', 'caos-signin__hero');
    hero.append(renderHero(SIGN_IN_HERO));

    const aside = el('div', 'caos-signin__aside');
    aside.append(buildAside(decision.auth));

    layout.append(hero, aside);
    ctx.mount.append(layout);
  },
};
